import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Library, Send, Share2 } from "lucide-react";
import { TRANSLATIONS, starThresholds } from "@versedrill/core";
import { Button } from "@/components/ui/button";
import type { GameResult } from "@/lib/useGame.ts";
import { shareResult, shareText } from "@/lib/share.ts";
import { useStore } from "@/lib/store.ts";
import { Countdown } from "./Countdown.tsx";
import { InstallCard } from "./InstallCard.tsx";
import { ReminderCard } from "./ReminderCard.tsx";
import { RedactedText } from "./Redacted.tsx";
import { StarRow } from "./StarRow.tsx";
import { StatsBlock } from "./StatsBlock.tsx";

export function ResultPanel({ number, result, redactedText, nextNumber }: { number: number; result: GameResult; redactedText: string; nextNumber: number | null }) {
  const streaks = useStore((s) => s.streaks);
  const version = useStore((s) => s.translation);
  const installDismissed = useStore((s) => s.installCardDismissed);
  const dismissInstall = useStore((s) => s.dismissInstallCard);
  const [shareState, setShareState] = useState<string | null>(null);
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    ref.current?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }, []);

  const share = async () => {
    const text = shareText({ number, text: redactedText, version: TRANSLATIONS[version].label, stars: result.stars, score: result.score, streak: streaks.currentStreak, url: window.location.origin });
    const r = await shareResult(text);
    setShareState(r === "copied" ? "Copied to clipboard" : r === "failed" ? "Couldn't copy — select and copy manually" : null);
    if (r === "copied") setTimeout(() => setShareState(null), 2500);
  };

  return (
    <section ref={ref} aria-labelledby="found-h" className="flex scroll-mt-[calc(var(--sticky-h,240px)+16px)] flex-col items-center gap-7 pb-16 pt-4 text-center">
      <div className="animate-ink-in">
        <p className="font-ui text-[0.7rem] font-semibold uppercase tracking-[0.24em] text-found">Found</p>
        <h2 id="found-h" className="font-display text-6xl italic leading-none text-brand sm:text-7xl">
          {result.reference}
        </h2>
      </div>

      <blockquote className="animate-leaf-in max-w-prose font-body text-[1.12rem] italic leading-relaxed text-foreground/85" style={{ ["--d" as string]: "160ms" }}>
        <RedactedText text={result.text} />
      </blockquote>

      {result.trivia && (
        <aside className="animate-leaf-in w-full max-w-prose rounded-lg border border-border border-l-[5px] border-l-brand bg-card px-5 py-4 text-left shadow-sm" style={{ ["--d" as string]: "260ms" }}>
          <p className="mb-1 font-ui text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-brand">In the margin</p>
          <p className="font-body text-[1.02rem] leading-relaxed">{result.trivia}</p>
        </aside>
      )}

      <div className="flex flex-col items-center gap-1">
        <StarRow count={result.stars} size="text-6xl" animate thresholds={starThresholds(result.possible)} />
        <p className="font-ui text-sm text-muted-foreground">
          <span className="font-semibold tabular-nums text-foreground">{result.score}</span> of <span className="tabular-nums">{result.possible}</span> points
        </p>
      </div>

      <StatsBlock />

      <div className="rounded-xl border border-border bg-card px-6 py-3 font-ui">
        <div className="text-xs text-muted-foreground">Next verse in</div>
        <div className="text-3xl font-semibold tabular-nums">
          <Countdown onZero={() => window.location.reload()} />
        </div>
      </div>

      <ReminderCard onlyWhenOff className="max-w-prose" />

      {!installDismissed && <InstallCard onDismiss={dismissInstall} className="w-full max-w-prose" />}

      <div className="flex w-full max-w-sm flex-col gap-2.5 font-ui">
        <Button size="lg" onClick={share} className="h-12 gap-2 text-base">
          <Share2 className="size-4" aria-hidden /> Share result
        </Button>
        <p role="status" className="h-4 text-xs text-muted-foreground">
          {shareState}
        </p>
        <Button size="lg" variant="outline" asChild className="h-12 gap-2 text-base">
          <Link to={`/send?b=${result.passage.bookId}&c=${result.passage.startChapter}&v=${result.passage.startVerse}&e=${result.passage.endVerse}`}>
            <Send className="size-4" aria-hidden /> Pass this verse to a friend
          </Link>
        </Button>
        {nextNumber !== null && (
          <Button size="lg" variant="outline" asChild className="h-12 text-base">
            <Link to="/" onClick={() => window.scrollTo({ top: 0 })}>
              {/* `/` opens the newest unplayed verse that is already out. After today's game that is an earlier day. */}
              {nextNumber < number ? "Play previous verses" : "Play the latest verse"}
            </Link>
          </Button>
        )}
        <div className="flex justify-center gap-1 text-muted-foreground">
          {number > 1 && (
            <Button variant="ghost" asChild>
              <Link to={`/challenges/${number - 1}`} onClick={() => window.scrollTo({ top: 0 })}>
                Previous verse
              </Link>
            </Button>
          )}
          <Button variant="ghost" asChild className="gap-2">
            <Link to="/challenges">
              <Library className="size-4" aria-hidden /> All verses
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
